import { supabase } from '../../supabase';

/**
 * KAN-196 (Stripe Ship 1) — the app's ONE door to the billing edge functions
 * and the billing-status RPC. Nothing here ever surfaces server text: a failed
 * call throws a `BillingApiError` whose `.code` is the edge function's CODE
 * (body `{ error: 'CODE' }`), which `userSafeErrorMessage` maps to localized
 * copy (see `BILLING_ERROR_CODES`). The server is the boundary — role checks
 * here are UX only. Stripe ids never reach the client.
 */
export class BillingApiError extends Error {
  constructor(code, status, payUrl = null) {
    super(code);
    this.name = 'BillingApiError';
    this.code = code;
    this.status = status ?? null;
    // Only ever a Stripe-hosted invoice page (see isSafePayUrl); null otherwise.
    this.payUrl = isSafePayUrl(payUrl) ? payUrl : null;
  }
}

const CODE_SHAPE = /^[A-Z][A-Z0-9_]{2,}$/;

/** The only places a "pay open bill" link may lead. */
export function isSafePayUrl(url) {
  return typeof url === 'string' && (url.startsWith('https://invoice.stripe.com/') || url.startsWith('https://pay.stripe.com/'));
}

async function readBody(error) {
  const ctx = error?.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const src = typeof ctx.clone === 'function' ? ctx.clone() : ctx;
      const body = await src.json();
      const c = typeof body?.error === 'string' ? body.error : body?.error?.code || body?.code;
      if (typeof c === 'string' && CODE_SHAPE.test(c)) return { code: c, payUrl: body?.pay_url ?? null };
    } catch (_e) { /* unreadable body: no code */ }
  }
  if (typeof error?.code === 'string' && CODE_SHAPE.test(error.code)) return { code: error.code, payUrl: null };
  return { code: null, payUrl: null };
}

export async function invoke(name, body = {}, method = 'POST') {
  let res;
  try {
    res = await supabase.functions.invoke(name, method === 'GET' ? { method } : { body });
  } catch (e) {
    throw new BillingApiError((await readBody(e)).code || 'NETWORK_ERROR');
  }
  const { data, error } = res || {};
  if (error) {
    const b = await readBody(error);
    throw new BillingApiError(b.code || 'BILLING_FAILED', error?.context?.status, b.payUrl);
  }
  if (data && typeof data.error === 'string') throw new BillingApiError(CODE_SHAPE.test(data.error) ? data.error : 'BILLING_FAILED', null, data.pay_url);
  return data || {};
}

/** Edge CODE -> localized copy for `userSafeErrorMessage({ codes })`. */
export const BILLING_ERROR_CODES = {
  BILLING_FREE: { id: 'billing.error.free', defaultMessage: 'This company never pays, so there is nothing to manage.' },
  ALREADY_SUBSCRIBED: { id: 'billing.error.alreadySubscribed', defaultMessage: 'This company is already subscribed. Refresh and try again.' },
  PLAN_NOT_SYNCED: { id: 'billing.error.planNotReady', defaultMessage: "This plan isn't available yet. Contact CrewVex." },
  BILLING_NOT_CONFIGURED: { id: 'billing.error.notConfigured', defaultMessage: "Billing isn't available right now. Try again later." },
  NOT_LINKED: { id: 'billing.error.notLinked', defaultMessage: 'There is no billing account yet. Subscribe first.' },
  ALREADY_ENDED: { id: 'billing.error.alreadyEnded', defaultMessage: 'This subscription already ended. Subscribe again to continue.' },
  MFA_REQUIRED: { id: 'billing.error.mfaRequired', defaultMessage: 'Confirm your two-step code to manage billing.' },
  BILLING_PROVIDER_ERROR: { id: 'billing.error.unavailable', defaultMessage: "Couldn't reach the payment provider. Try again." },
  CANCEL_PENDING: { id: 'billing.error.cancelPending', defaultMessage: 'This subscription is already set to end. Resume it first.' },
  SUBSCRIPTION_NOT_ACTIVE: { id: 'billing.error.notActive', defaultMessage: "This subscription isn't active right now." },
  NOT_SUBSCRIBED: { id: 'billing.error.notSubscribed', defaultMessage: "This company isn't subscribed yet. Subscribe first." },
  STRIPE_MANAGED: { id: 'billing.error.stripeManaged', defaultMessage: 'Billing for this company is managed by its subscription, so it can’t be edited here.' },
  CHECKOUT_IN_PROGRESS: { id: 'billing.error.checkoutInProgress', defaultMessage: 'Checkout is already opening. Try again in a moment.' },
  CUSTOMER_MISSING: { id: 'billing.error.customerMissing', defaultMessage: 'Your billing account needs attention. Contact CrewVex at support@crewvex.com.' },
  PLAN_TOO_SMALL: { id: 'billing.error.planTooSmall', defaultMessage: "That plan doesn't fit your team right now. Pick another plan." },
  FORBIDDEN: { id: 'billing.error.ownerOnly', defaultMessage: 'Only the owner can do this.' },
  UNPAID_BALANCE: { id: 'billing.error.unpaidBalance', defaultMessage: 'You have an unpaid bill. Pay it to subscribe again.' },
};

/** The company's own billing status (owner/admin). Throws BillingApiError; never returns Stripe ids. */
export async function getMyBillingStatus() {
  let res;
  try {
    res = await supabase.rpc('get_my_billing_status');
  } catch (_e) {
    throw new BillingApiError('NETWORK_ERROR');
  }
  const { data, error } = res || {};
  if (error || !data || typeof data !== 'object') throw new BillingApiError(error?.code === '42501' ? 'FORBIDDEN' : 'BILLING_FAILED');
  return normalizeStatus(data);
}

const str = (v) => (v === undefined || v === null || v === '' ? null : String(v));

export function normalizeStatus(d) {
  return {
    billingType: str(d.billing_type),
    payingStatus: str(d.paying_status),
    trialEndsAt: str(d.trial_ends_at),
    planCode: str(d.plan_code),
    isOwner: d.is_owner === true,
    // KAN-855: the owner's display name (never the email); null until the server sends it.
    ownerName: str(d.owner_name),
    // An ownerless company (no default admin): never "ask the owner". Only an explicit false counts.
    hasOwner: d.has_owner !== false,
    stripeLinked: d.stripe_linked === true,
    stripeStatus: str(d.stripe_status),
    billingStartsOn: str(d.billing_starts_on),
    cancelAt: str(d.cancel_at),
    scheduledPlanCode: str(d.scheduled_plan_code),
    scheduledEffectiveOn: str(d.scheduled_effective_on),
    pastDueSince: str(d.past_due_since),
    paidThrough: str(d.paid_through),
    companyToday: str(d.company_today),
    // Optional: the sellable plans with their price fields, when the server provides them.
    // Fail closed: only an explicit true turns the Stripe actions on (Prod stays on the email path until live billing ships).
    billingAvailable: d.billing_available === true,
    usage: d.usage && typeof d.usage === 'object'
      ? {
        officeUsed: Number(d.usage.office_used) || 0,
        workerUsed: Number(d.usage.worker_used) || 0,
        poolsThisMonth: Number(d.usage.pools_this_month) || 0,
        poolsLastMonth: Number(d.usage.pools_last_month) || 0,
      }
      : null,
    hasPriorSubscription: d.has_prior_subscription === true,
    plans: Array.isArray(d.plans) ? d.plans : null,
  };
}

const planBody = (planCode) => ({ plan_code: planCode });

/** @returns {Promise<{url: string}>} Stripe Checkout (subscribe / resubscribe). */
export const startCheckout = (planCode) => invoke('billing-checkout', planBody(planCode));
/** @returns {Promise<{url: string}>} Stripe portal: card + invoices only. */
export const openPortal = () => invoke('billing-portal');
/** @returns {Promise<{effective_on: string}>} Owner only. */
export const changePlan = (planCode) => invoke('billing-change-plan', planBody(planCode));
/** @returns {Promise<{cancel_at: string}>} Owner only. */
export const cancelSubscription = ({ reasons = [], note = '' } = {}) => invoke('billing-cancel', { reasons, ...(note ? { note } : {}) });
/** Owner only. */
export const resumeSubscription = () => invoke('billing-resume');
/** @returns {Promise<{stripe_status: string|null}>} re-reads the subscription from Stripe. */
export const refreshBilling = () => invoke('billing-refresh');

/** Platform (root): per-plan Stripe sync state. */
export async function getStripeSync() {
  const d = await invoke('platform-stripe-sync', undefined, 'GET');
  return Array.isArray(d) ? d : (Array.isArray(d.plans) ? d.plans : []);
}
/** Platform (root): create/refresh the Stripe catalog. */
export async function runStripeSync() {
  const d = await invoke('platform-stripe-sync', {});
  return Array.isArray(d) ? d : (Array.isArray(d.plans) ? d.plans : []);
}
